import writeXlsxFile from "write-excel-file";
import { dataFormat } from "../../../shared/functions/Directives";
import { IEmployeeTableRowItem } from "../../../shared/functions/scorecard-pdf/IndividualPerformanceAgreementDocument";
import { fullPerspectiveName } from "../../../shared/interfaces/IPerspectiveTabs";
import { IMeasure } from "../../../shared/models/Measure";
import { IObjective } from "../../../shared/models/Objective";
import {
  q4EmpRating,
  q4SuperRating,
  q4FinalRating,
  rateColor,
} from "./Scorecard";

//#region Interfaces and Constants
interface ExcelCell {
  value: any;
  type?: StringConstructor | NumberConstructor | DateConstructor;
  format?: string;
  rowSpan?: number;
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  align?: "left" | "center" | "right";
  alignVertical?: "top" | "center" | "bottom";
  borderColor?: string;
  wrap?: boolean;
  fontWeight?: "bold" | "normal";
}

interface SchemaColumn {
  column: string;
  type: StringConstructor | NumberConstructor | DateConstructor;
  color?: string;
  backgroundColor?: string;
  fontWeight?: "bold" | "normal";
  width: number;
  fontSize?: number;
  align?: "left" | "center" | "right";
  alignVertical?: "top" | "center" | "bottom";
  wrap?: boolean;
  valueGetter: (row: IEmployeeTableRowItem) => any;
  backgroundColorGetter?: (rating: number, hasValue?: boolean) => string;
  format?: string;
  dynamicType?: boolean;
  mergeGroup?: string[];
}

const PERSPECTIVE_ORDER = ["F", "C", "I", "L"];

const createSchemaColumn = (
  column: string,
  valueGetter: SchemaColumn["valueGetter"],
  config: Partial<SchemaColumn>
): SchemaColumn => ({
  column,
  valueGetter,
  type: String,
  fontWeight: "normal",
  alignVertical: "top",
  wrap: true,
  width: 20,
  backgroundColor: "#f2f2f2",
  ...config,
});

const EXCEL_SCHEMA = [
  createSchemaColumn("Perspectives", (row) => row.perspective, {
    mergeGroup: ["perspective"],
    fontWeight: "bold",
    width: 20,
  }),
  createSchemaColumn(
    "Contributory Departmental Objective",
    (row) => row.strategicObjective,
    {
      mergeGroup: ["strategic"],
      fontWeight: "bold",
      width: 40,
    }
  ),
  createSchemaColumn(
    "Individual Scorecard Contribution",
    (row) => row.contributoryObjective,
    {
      mergeGroup: ["contributory"],
      fontWeight: "bold",
      width: 40,
    }
  ),
  createSchemaColumn("Weight", (row) => row.weight, {
    mergeGroup: ["contributory"],
    type: Number,
    format: "#,##0",
    fontWeight: "bold",
    width: 10,
  }),
  createSchemaColumn("Draft Comments", (row) => row.draftComment, {
    mergeGroup: ["contributory"],
    width: 40,
  }),
  createSchemaColumn("Midterm Comments", (row) => row.midComment, {
    mergeGroup: ["contributory"],
    width: 40,
  }),
  createSchemaColumn("Assessment Comment", (row) => row.assessComment, {
    mergeGroup: ["contributory"],
    width: 40,
  }),
  createSchemaColumn("Measures/KPI", (row) => row.measure, { width: 25 }),
  createSchemaColumn("Baseline", (row) => row.baseline, {
    dynamicType: true,
    width: 15,
  }),
  createSchemaColumn("Annual Target", (row) => row.annualTarget, {
    dynamicType: true,
    width: 15,
  }),
  createSchemaColumn("Rating Scale 1-5", (row) => row.ratingScale, {
    width: 20,
  }),
  createSchemaColumn("Key Initiatives", (row) => row.keyInitiatives, {
    width: 40,
  }),
  createSchemaColumn(
    "Target Date",
    (row) => (row.targetDate ? new Date(row.targetDate) : null),
    {
      type: Date,
      format: "dd mmmm yyyy",
      width: 15,
    }
  ),
  createSchemaColumn("Source of Evidence", (row) => row.sourceOfEvidence, {
    width: 25,
  }),
  createSchemaColumn("Comments", (row) => row.comments, { width: 40 }),
  createSchemaColumn("Midterm E Rating", (row) => row.autoRating, {
    type: Number,
    width: 15,
    align: "center",
    alignVertical: "center",
    backgroundColorGetter: (rating) => rateColor(rating, true, true),
    fontSize: 16,
    fontWeight: "bold",
    format: "#,##0",
    color: "#fffff",
  }),
  createSchemaColumn("Midterm S Rating", (row) => row.supervisorRating, {
    type: Number,
    width: 15,
    align: "center",
    alignVertical: "center",
    backgroundColorGetter: (rating) => rateColor(rating, true, true),
    fontSize: 16,
    fontWeight: "bold",
    format: "#,##0",
    color: "#fffff",
  }),
  createSchemaColumn("Midterm F Rating", (row) => row.finalRating, {
    type: Number,
    width: 15,
    align: "center",
    alignVertical: "center",
    backgroundColorGetter: (rating) => rateColor(rating, true, true),
    fontSize: 16,
    fontWeight: "bold",
    format: "#,##0",
    color: "#fffff",
  }),
  createSchemaColumn("Assessment E Rating", (row) => row.autoRating2, {
    type: Number,
    width: 15,
    align: "center",
    alignVertical: "center",
    backgroundColorGetter: (rating) => rateColor(rating, true, true),
    fontSize: 16,
    fontWeight: "bold",
    format: "#,##0",
    color: "#fffff",
  }),
  createSchemaColumn("Assessment S Rating", (row) => row.supervisorRating2, {
    type: Number,
    width: 15,
    align: "center",
    alignVertical: "center",
    backgroundColorGetter: (rating) => rateColor(rating, true, true),
    fontSize: 16,
    fontWeight: "bold",
    format: "#,##0",
    color: "#fffff",
  }),
  createSchemaColumn("Assessment F Rating", (row) => row.finalRating2, {
    type: Number,
    width: 15,
    align: "center",
    alignVertical: "center",
    backgroundColorGetter: (rating) => rateColor(rating, true, true),
    fontSize: 16,
    fontWeight: "bold",
    format: "#,##0",
    color: "#fffff",
  }),
];

const COLUMN_WIDTHS = EXCEL_SCHEMA.map(({ width }) => ({ width }));
//#endregion

//#region Data Preparation
const createRatingScale = (measure: IMeasure): string => {
  const ratings = [1, 2, 3, 4, 5].map((n) => ({
    value: measure[`rating${n}` as keyof IMeasure],
    label: `${n} = ${dataFormat(
      measure.dataType,
      measure[`rating${n}` as keyof IMeasure] as number | null | string,
      measure.dataSymbol
    )}`,
  }));
  return ratings
    .filter((r) => r.value)
    .map((r) => r.label)
    .join("\n");
};

const prepareEmployeeData = (
  objectives: IObjective[],
  contributoryObjectives: IObjective[],
  measures: IMeasure[]
): IEmployeeTableRowItem[] => {
  return measures
    .map((measure) => {
      const contributory = contributoryObjectives.find(
        (o) => o.id === measure.objective
      );
      const strategic = objectives.find((o) => o.id === contributory?.parent);

      // Safe value conversion
      const safeString = (value: any) => value?.toString()?.trim() || "-";
      const safeNumber = (value: any) => {
        const num = Number(value);
        return isNaN(num) ? null : num;
      };

      const weight = contributory?.weight || 0;

      return {
        perspective: safeString(
          fullPerspectiveName(contributory?.perspective ?? "-")
        ),
        strategicObjective: safeString(strategic?.description),
        contributoryObjective: contributory?.description || "-",
        weight: safeNumber(weight),
        draftComment: safeString(contributory?.draftComment),
        midComment: safeString(contributory?.midComment),
        assessComment: safeString(contributory?.assessComment),
        measure: safeString(measure.description),
        baseline: safeString(measure.baseline),
        annualTarget: safeString(measure.annualTarget),
        ratingScale: safeString(createRatingScale(measure)),
        keyInitiatives: safeString(measure.activities),
        targetDate: measure.targetDate,
        sourceOfEvidence: safeString(measure.sourceOfEvidence),
        comments: safeString(measure.comments),
        dataType: measure.dataType,
        dataSymbol: measure.dataSymbol,
        autoRating: safeNumber(measure.autoRating),
        supervisorRating: safeNumber(measure.supervisorRating),
        finalRating: safeNumber(measure.finalRating),
        autoRating2: safeNumber(measure.autoRating2),
        supervisorRating2: safeNumber(measure.supervisorRating2),
        finalRating2: safeNumber(measure.finalRating2),
      } as IEmployeeTableRowItem;
    })
    .sort((a, b) => {
      const perspectiveCompare =
        PERSPECTIVE_ORDER.indexOf(a.perspective.charAt(0)) -
        PERSPECTIVE_ORDER.indexOf(b.perspective.charAt(0));

      const strategicCompare =
        perspectiveCompare !== 0
          ? perspectiveCompare
          : a.strategicObjective.localeCompare(b.strategicObjective);

      return strategicCompare !== 0
        ? strategicCompare
        : a.contributoryObjective.localeCompare(b.contributoryObjective);
    });
};
//#endregion

//#region Excel Formatting
const createBaseCell = (
  column: SchemaColumn,
  value: any,
  rowSpan?: number
): ExcelCell => {
  // Validate and convert values based on column type
  let processedValue: any = value;
  let cellType = column.type;

  try {
    // Handle null/undefined values
    if (value === null || value === undefined) {
      return {
        value: "",
        rowSpan,
        backgroundColor: column.backgroundColor,
        borderColor: "#555555",
        wrap: column.wrap,
      };
    }

    // Type conversion and validation
    switch (column.type) {
      case String:
        processedValue = String(value);
        break;

      case Number:
        processedValue =
          typeof value === "string"
            ? Number(value.replace(/[^0-9.%]/g, ""))
            : Number(value);
        processedValue = isNaN(processedValue) ? null : processedValue;
        break;

      case Date:
        processedValue = value instanceof Date ? value : new Date(value);
        if (isNaN(processedValue.getTime())) {
          processedValue = null;
          cellType = String;
        }
        break;

      default:
        processedValue = String(value);
        cellType = String;
    }

    // Special handling for percentage values
    if (column.format?.includes("%") && typeof processedValue === "number") {
      processedValue = processedValue / 100;
    }
  } catch (error) {
    console.warn(
      `Error processing value ${value} for column ${column.column}:`,
      error
    );
    processedValue = "Invalid Value";
    cellType = String;
  }

  return {
    value: processedValue,
    type: cellType,
    format: column.format,
    rowSpan,
    fontSize: column.fontSize,
    color: column.color,
    backgroundColor: column.backgroundColor,
    align: column.align,
    alignVertical: column.alignVertical,
    borderColor: "#555555",
    wrap: column.wrap,
    fontWeight: column.fontWeight,
  };
};

const formatExcelData = (rows: IEmployeeTableRowItem[]) => {
  const excelData: ExcelCell[][] = [
    EXCEL_SCHEMA.map((col) => ({
      value: col.column,
      fontWeight: "bold",
      fontSize: 12,
      backgroundColor: col.backgroundColor,
      wrap: col.wrap,
    })),
  ];

  // Group data hierarchically
  const groupedData = new Map<
    string,
    {
      totalRows: number;
      strategicMap: Map<
        string,
        {
          totalRows: number;
          contributoryMap: Map<
            string,
            {
              weight: number;
              comments: {
                draft: string;
                mid: string;
                assess: string;
              };
              measures: IEmployeeTableRowItem[];
            }
          >;
        }
      >;
    }
  >();

  // Organize data into hierarchical structure
  rows.forEach((row) => {
    // Perspective level
    if (!groupedData.has(row.perspective)) {
      groupedData.set(row.perspective, {
        totalRows: 0,
        strategicMap: new Map(),
      });
    }
    const perspectiveGroup = groupedData.get(row.perspective)!;

    // Strategic objective level
    if (!perspectiveGroup.strategicMap.has(row.strategicObjective)) {
      perspectiveGroup.strategicMap.set(row.strategicObjective, {
        totalRows: 0,
        contributoryMap: new Map(),
      });
    }
    const strategicGroup = perspectiveGroup.strategicMap.get(
      row.strategicObjective
    )!;

    // Contributory objective level
    if (!strategicGroup.contributoryMap.has(row.contributoryObjective)) {
      strategicGroup.contributoryMap.set(row.contributoryObjective, {
        weight: row.weight,
        comments: {
          draft: row.draftComment,
          mid: row.midComment,
          assess: row.assessComment,
        },
        measures: [],
      });
    }
    const contributoryGroup = strategicGroup.contributoryMap.get(
      row.contributoryObjective
    )!;

    contributoryGroup.measures.push(row);
    strategicGroup.totalRows++;
    perspectiveGroup.totalRows++;
  });

  // Generate Excel rows
  groupedData.forEach((perspectiveGroup, perspective) => {
    let perspectiveRowsAdded = 0;

    perspectiveGroup.strategicMap.forEach(
      (strategicGroup, strategicObjective) => {
        let strategicRowsAdded = 0;

        strategicGroup.contributoryMap.forEach(
          (contributoryGroup, contributoryObjective) => {
            const measures = contributoryGroup.measures;
            const contributoryRowSpan = measures.length;

            measures.forEach((measure, measureIndex) => {
              const excelRow: (ExcelCell | null)[] = [];
              const isFirstPerspectiveRow = perspectiveRowsAdded === 0;
              const isFirstStrategicRow = strategicRowsAdded === 0;
              const isFirstContributoryRow = measureIndex === 0;

              EXCEL_SCHEMA.forEach((col) => {
                // Handle perspective merge
                if (col.mergeGroup?.includes("perspective")) {
                  excelRow.push(
                    isFirstPerspectiveRow
                      ? createBaseCell(
                          col,
                          perspective,
                          perspectiveGroup.totalRows
                        )
                      : null
                  );
                  return;
                }

                // Handle strategic objective merge
                if (col.mergeGroup?.includes("strategic")) {
                  excelRow.push(
                    isFirstStrategicRow
                      ? createBaseCell(
                          col,
                          strategicObjective,
                          strategicGroup.totalRows
                        )
                      : null
                  );
                  return;
                }

                // Handle contributory group merge (weight + comments)
                if (col.mergeGroup?.includes("contributory")) {
                  if (isFirstContributoryRow) {
                    const value = col.valueGetter({
                      ...measure,
                      weight: contributoryGroup.weight,
                      draftComment: contributoryGroup.comments.draft,
                      midComment: contributoryGroup.comments.mid,
                      assessComment: contributoryGroup.comments.assess,
                    });

                    excelRow.push(
                      createBaseCell(col, value, contributoryRowSpan)
                    );
                  } else {
                    excelRow.push(null);
                  }
                  return;
                }

                // Regular cells
                const rawValue = col.valueGetter(measure);
                const cell = createBaseCell(col, rawValue);

                // Handle dynamic types
                if (col.dynamicType && measure.dataType) {
                  if (
                    ["Currency", "Number", "Custom"].includes(measure.dataType)
                  ) {
                    cell.type = Number;
                    cell.value = isNaN(rawValue) ? null : Number(rawValue);
                    cell.format =
                      measure.dataType === "Custom"
                        ? `#,##0${measure.dataSymbol || ""}`
                        : "#,##0.00";
                  } else if (measure.dataType === "Percentage") {
                    cell.type = Number;
                    cell.value = isNaN(rawValue)
                      ? null
                      : Number(rawValue) / 100;
                    cell.format = "0.0%";
                  } else if (measure.dataType === "Date") {
                    cell.type = Date;
                    cell.value = rawValue ? new Date(rawValue) : null;
                  }
                }

                // Handle rating colors
                if (col.backgroundColorGetter) {
                  cell.backgroundColor = col.backgroundColorGetter(
                    cell.value,
                    cell.value !== null
                  );
                }

                excelRow.push(cell);
              });

              excelData.push(excelRow as ExcelCell[]);
              perspectiveRowsAdded++;
              strategicRowsAdded++;
            });
          }
        );
      }
    );
  });

  return excelData;
};
//#endregion

const createFooterRow = (measures: IMeasure[]): ExcelCell[] => {
  const emptyCells = Array(EXCEL_SCHEMA.length - 3).fill({
    value: "",
    backgroundColor: "#f2f2f2",
  });

  return [
    ...emptyCells,
    {
      value: q4EmpRating(measures).toFixed(2),
      fontWeight: "bold",
      backgroundColor: "#f2f2f2",
    },
    {
      value: q4SuperRating(measures).toFixed(2),
      fontWeight: "bold",
      backgroundColor: "#f2f2f2",
    },
    {
      value: q4FinalRating(measures).toFixed(2),
      fontWeight: "bold",
      backgroundColor: "#f2f2f2",
    },
  ] as ExcelCell[];
};
//#endregion

export const exportEmployeeScorecardExcel = async (
  title: string,
  objectives: IObjective[],
  contributoryObjectives: IObjective[],
  measures: IMeasure[]
): Promise<boolean> => {
  try {
    const data = formatExcelData(
      prepareEmployeeData(objectives, contributoryObjectives, measures)
    );
    data.push(createFooterRow(measures));

    await writeXlsxFile(data as any, {
      columns: COLUMN_WIDTHS,
      fileName: `${title}.xlsx`,
      sheet: "Employee Scorecard",
      fontFamily: "Candara",
      fontSize: 12,
    });

    return true;
  } catch (error) {
    console.error("Excel export failed:", error);
    return false;
  }
};
